/**
 * FormContext Component
 * Provides form context to custom form fields
 */
import React, { createContext, useContext } from 'react'
import { Control, FieldErrors } from 'react-hook-form'
import { DataSourcesState } from './types'

// Form context type
interface FormContextType {
  control: Control<any>
  errors: FieldErrors
  values: Record<string, any>
  readOnly?: boolean | string[]
  originalValues?: Record<string, any>
  dataSources?: DataSourcesState // Data source state
  reloadDataSource?: (key: string) => Promise<void> // Reload data source
  onBlur?: (
    field: { name: string; value: any },
    values: Record<string, any>
  ) => void // Blur event handler
}

// Create context
const FormContext = createContext<FormContextType | null>(null)

// Context provider component
interface FormContextProviderProps {
  control: Control<any>
  errors: FieldErrors
  values: Record<string, any>
  readOnly?: boolean | string[]
  originalValues?: Record<string, any>
  dataSources?: DataSourcesState
  reloadDataSource?: (key: string) => Promise<void>
  onBlur?: (
    field: { name: string; value: any },
    values: Record<string, any>
  ) => void
  children: React.ReactNode
}

export const FormContextProvider: React.FC<FormContextProviderProps> = ({
  control,
  errors,
  values,
  readOnly,
  originalValues,
  dataSources,
  reloadDataSource,
  onBlur,
  children
}) => {
  return (
    <FormContext.Provider
      value={{
        control,
        errors,
        values,
        readOnly,
        originalValues,
        dataSources,
        reloadDataSource,
        onBlur
      }}>
      {children}
    </FormContext.Provider>
  )
}

// Hook to use context
export const useFormContext = () => {
  const context = useContext(FormContext)
  if (!context) {
    throw new Error('useFormContext must be used within a FormContextProvider')
  }
  return context
}

export default FormContext
